import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { getServiceTotal, sumAmounts } from './billDetailsUtils';
export const round2 = value => Math.round(value * 100) / 100;

/** Empty inputs mean "no value" rather than zero, so totals do not silently drop to 0. */
export function toNumber(value) {
  if (value === '' || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * Keep the derived amounts in step with whatever the user just typed: a service subtotal follows its
 * line items, a property subtotal follows its services, and the total due follows the properties.
 * Services without line items keep their extracted subtotal, since there is nothing to sum.
 */
export function withRecalculatedTotals(data) {
  if (!data) return data;
  const properties = (data.properties || []).map(property => {
    const services = (property.services || []).map(service => {
      const lineItems = service.lineItems || [];
      return lineItems.length ? _objectSpread(_objectSpread({}, service), {}, {
        subtotal: round2(sumAmounts(lineItems))
      }) : service;
    });
    const subtotal = services.reduce((acc, service) => {
      const total = getServiceTotal(service);
      return acc + (total !== null && total !== void 0 ? total : 0);
    }, 0);
    return _objectSpread(_objectSpread({}, property), {}, {
      services,
      subtotal: round2(subtotal)
    });
  });
  if (!properties.length) return _objectSpread({}, data);
  return _objectSpread(_objectSpread({}, data), {}, {
    properties,
    totalAmountDue: round2(properties.reduce((acc, p) => acc + p.subtotal, 0))
  });
}
function mapProperties(data, mapper) {
  return _objectSpread(_objectSpread({}, data), {}, {
    properties: ((data === null || data === void 0 ? void 0 : data.properties) || []).map(mapper)
  });
}

/** For edits that move money: the amounts they touch are rolled up before the next render. */
function mapPropertyAmounts(data, mapper) {
  return withRecalculatedTotals(mapProperties(data, mapper));
}

/** Summary fields carry no amounts, so editing them must not touch the extracted totals. */
export function updateBillField(data, patch) {
  return _objectSpread(_objectSpread({}, data), patch);
}

/** Only editable while no line items exist to derive the total from; kept raw until save. */
export function updateTotalDue(data, value) {
  return _objectSpread(_objectSpread({}, data), {}, {
    totalAmountDue: value === '' ? null : value
  });
}
export function removeProperty(data, propertyIndex) {
  return withRecalculatedTotals(_objectSpread(_objectSpread({}, data), {}, {
    properties: ((data === null || data === void 0 ? void 0 : data.properties) || []).filter((property, index) => index !== propertyIndex)
  }));
}
export function updateService(data, propertyIndex, serviceIndex, patch) {
  return mapPropertyAmounts(data, (property, index) => {
    if (index !== propertyIndex) return property;
    const services = (property.services || []).map((service, i) => i === serviceIndex ? _objectSpread(_objectSpread({}, service), patch) : service);
    return _objectSpread(_objectSpread({}, property), {}, {
      services
    });
  });
}

/**
 * The summary states one service period for the bill, so moving it moves every utility on it. A
 * utility that carried its own dates is overwritten, which is what a single row can honestly mean.
 */
export function setServiceDates(data, patch) {
  return mapProperties(_objectSpread(_objectSpread({}, data), patch), property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => _objectSpread(_objectSpread({}, service), patch))
  }));
}

/** Dropping a utility chip drops that utility everywhere, which is what the summary row promises. */
export function removeServiceType(data, serviceType) {
  return mapPropertyAmounts(data, property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).filter(service => service.serviceType !== serviceType)
  }));
}
export function setGlCodeForServiceType(data, serviceType, glCode) {
  return mapProperties(data, property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => service.serviceType === serviceType ? _objectSpread(_objectSpread({}, service), {}, {
      glCode
    }) : service)
  }));
}
function mapLineItems(data, propertyIndex, serviceIndex, mapper) {
  return mapPropertyAmounts(data, (property, index) => {
    if (index !== propertyIndex) return property;
    const services = (property.services || []).map((service, i) => i === serviceIndex ? _objectSpread(_objectSpread({}, service), {}, {
      lineItems: mapper(service.lineItems || [])
    }) : service);
    return _objectSpread(_objectSpread({}, property), {}, {
      services
    });
  });
}
export function updateLineItem(data, propertyIndex, serviceIndex, lineIndex, patch) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => lineItems.map((line, index) => index === lineIndex ? _objectSpread(_objectSpread({}, line), patch) : line));
}
export function addLineItem(data, propertyIndex, serviceIndex) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => [...lineItems, {
    amount: null,
    description: ''
  }]);
}
export function removeLineItem(data, propertyIndex, serviceIndex, lineIndex) {
  return mapLineItems(data, propertyIndex, serviceIndex, lineItems => lineItems.filter((line, index) => index !== lineIndex));
}

/**
 * While typing, amount fields hold the raw text so partial input such as "12." survives a keystroke.
 * Saving turns those back into numbers, so nothing downstream has to parse strings. Totals are left
 * as they are: they were already recalculated with every edit.
 */
export function normalizeAmounts(data) {
  if (!data) return data;
  const properties = (data.properties || []).map(property => _objectSpread(_objectSpread({}, property), {}, {
    services: (property.services || []).map(service => _objectSpread(_objectSpread({}, service), {}, {
      lineItems: (service.lineItems || []).map(line => _objectSpread(_objectSpread({}, line), {}, {
        amount: toNumber(line.amount)
      }))
    }))
  }));
  return _objectSpread(_objectSpread({}, data), {}, {
    properties,
    totalAmountDue: toNumber(data.totalAmountDue)
  });
}

/** Allocation inputs are stored per property/utility, since each table is edited on its own. */
export function getAllocationSettingKey(propertyIndex, serviceType) {
  return "".concat(propertyIndex, ":").concat(serviceType || '');
}
export function getAllocationSetting(settings, propertyIndex, serviceType) {
  return (settings === null || settings === void 0 ? void 0 : settings[getAllocationSettingKey(propertyIndex, serviceType)]) || {};
}
export function withAllocationSetting(settings, propertyIndex, serviceType, patch) {
  const key = getAllocationSettingKey(propertyIndex, serviceType);
  return _objectSpread(_objectSpread({}, settings), {}, {
    [key]: _objectSpread(_objectSpread({}, (settings === null || settings === void 0 ? void 0 : settings[key]) || {}), patch)
  });
}